/**
 * Internal governance citation card (DC-1670, v2.5 Pillar 5).
 *
 * The `source: 'internal'` variant of a drawer citation — "here is what YOUR
 * governance says", as opposed to the framework card's "here is what a generic
 * body of knowledge says". One card serves both the DQ category drawer and the
 * ML metric drawer (the two citation shapes are field-identical).
 *
 * Distinguished from the framework card on four redundant channels (UX spec §1.4):
 * a 3px terracotta left rule, the INTERNAL text chip, a ◆ glyph, and grouping
 * under the "Your governance" subgroup the drawer renders. Cream/teal tokens only.
 *
 * UX spec: docs/design/v2.5-governance-citations-ux-spec.md §1.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { DataGovernanceCitation } from '../../types/dataQualityCategory';
import './citation-card.css';

/** high ≥ .75, med ≥ .4, low otherwise; null → no meter. */
function confidenceBand(c: number | undefined): { level: 1 | 2 | 3; label: string } | null {
  if (c === undefined || c === null || !Number.isFinite(c)) return null;
  if (c >= 0.75) return { level: 3, label: 'High' };
  if (c >= 0.4) return { level: 2, label: 'Medium' };
  return { level: 1, label: 'Low' };
}

function formatDate(iso: string | undefined): { display: string; title: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    display: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
    title: d.toISOString(),
  };
}

export function InternalCitationCard({ citation }: { citation: DataGovernanceCitation }): React.ReactElement {
  const band = confidenceBand(citation.confidence);
  const date = formatDate(citation.uploadedAt);
  const title = citation.documentTitle || citation.document;
  const hasFooter = Boolean(citation.uploadedBy) || Boolean(date) || Boolean(band);

  return (
    <div className="ic-card">
      <div className="ic-head">
        <span className="ic-chip">
          <span aria-hidden="true" className="ic-chip-glyph">◆</span> INTERNAL
        </span>
      </div>
      <p className="ic-doc" title={title}>{title}</p>
      {citation.section && <p className="ic-section">{citation.section}</p>}
      <blockquote className="ic-quote">{citation.text}</blockquote>

      {hasFooter && (
        <div className="ic-footer">
          <span className="ic-attr">
            {citation.uploadedBy ? `Uploaded by ${citation.uploadedBy}` : ''}
            {citation.uploadedBy && date ? ' · ' : ''}
            {date && <time dateTime={citation.uploadedAt} title={date.title}>{date.display}</time>}
          </span>
          {band && (
            <span className="ic-conf" aria-label={`Mapping confidence: ${band.label}`}>
              <span className={`ic-meter ic-meter--${band.level}`} aria-hidden="true"><i /><i /><i /></span>
              <span className="ic-conf-text">{band.label}</span>
            </span>
          )}
        </div>
      )}

      {citation.documentId && (
        <Link className="ic-link" to={`/settings/governance/documents/${citation.documentId}`}>
          Open governance document
        </Link>
      )}
    </div>
  );
}

export default InternalCitationCard;
